<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { Dropdown as VDropdown } from 'floating-vue';
import Icon from 'next/icon/Icon.vue';
import {
  useEventStageColors,
  resolveStatusOptionColor,
} from './useEventStageColors';

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  customStatus: {
    type: String,
    default: null,
  },
  statusOptions: {
    type: Array,
    default: () => [],
  },
  accentColor: {
    type: String,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();
const { stages, getDefaultStageColor } = useEventStageColors();
const isDropdownOpen = ref(false);
const query = ref('');
const listRef = ref(null);
const searchRef = ref(null);
const showSearch = ref(false);

const optionLabel = option =>
  option.label || option.key.replace('_', ' ');

const builtInOptions = computed(() =>
  stages.map(stage => ({
    type: 'system',
    key: stage,
    label: stage.replace('_', ' '),
    system_stage: stage,
    anchor_system_stage: stage,
    resolved_color: getDefaultStageColor(stage),
  }))
);

const normalizedOptions = computed(() => {
  if (props.statusOptions.length) {
    return props.statusOptions;
  }
  return builtInOptions.value;
});

const currentOption = computed(() => {
  const custom = normalizedOptions.value.find(
    option => option.type === 'custom' && option.key === props.customStatus
  );
  if (custom) return custom;
  return (
    normalizedOptions.value.find(
      option =>
        option.type === 'system' && option.system_stage === props.modelValue
    ) || normalizedOptions.value[0]
  );
});

const filteredOptions = computed(() => {
  const q = query.value.trim().toLowerCase();
  if (!showSearch.value || !q) return normalizedOptions.value;
  return normalizedOptions.value.filter(option =>
    optionLabel(option).toLowerCase().includes(q)
  );
});

const revealSearchIfOverflowing = (frame = 0) => {
  if (!isDropdownOpen.value || showSearch.value || frame > 5) return;
  const el = listRef.value;
  if (!el?.clientHeight || el.scrollHeight <= el.clientHeight) {
    if (frame < 5) requestAnimationFrame(() => revealSearchIfOverflowing(frame + 1));
    return;
  }
  showSearch.value = true;
  nextTick(() => searchRef.value?.focus());
};

watch(isDropdownOpen, open => {
  if (open) return;
  query.value = '';
  showSearch.value = false;
});

const selectedDotColor = computed(
  () =>
    props.accentColor ||
    resolveStatusOptionColor(currentOption.value) ||
    getDefaultStageColor(props.modelValue)
);

const selectStage = option => {
  if (option.type === 'custom') {
    emit('update:modelValue', {
      system_stage: option.system_stage,
      custom_status: option.key,
    });
  } else {
    emit('update:modelValue', {
      system_stage: option.system_stage,
      custom_status: null,
    });
  }
  isDropdownOpen.value = false;
};

const sizeClasses = computed(() =>
  props.size === 'sm' ? 'h-8 px-3 gap-1.5 text-sm' : 'h-10 px-3.5 gap-2 text-sm'
);
</script>

<template>
  <VDropdown
    v-model:shown="isDropdownOpen"
    :triggers="disabled ? [] : ['click']"
    placement="bottom-end"
    :distance="8"
    :arrow="false"
    no-auto-focus
    popper-class="event-menu-popper"
    @apply-show="() => revealSearchIfOverflowing()"
  >
    <button
      type="button"
      class="font-bold rounded-full capitalize flex items-center bg-white dark:bg-n-solid-2 outline outline-1 outline-n-slate-6 text-n-slate-12 hover:outline-n-slate-7 transition-colors"
      :class="[sizeClasses, { 'opacity-50 cursor-not-allowed': disabled }]"
      :disabled="disabled"
      @click.stop
      @mousedown.stop
    >
      <span
        class="size-2.5 rounded-full flex-shrink-0"
        :style="{ backgroundColor: selectedDotColor }"
      />
      <span class="truncate max-w-32 text-base">{{
        currentOption?.label || modelValue.replace('_', ' ')
      }}</span>
      <Icon
        :icon="isDropdownOpen ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        class="size-3.5 text-n-slate-11"
      />
    </button>
    <template #popper>
      <div
        class="bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-75 dark:border-slate-800 p-2 flex flex-col gap-1 min-w-44"
        @click.stop
        @mousedown.stop
      >
        <div v-if="showSearch" class="relative shrink-0">
          <span
            class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-slate-400 pointer-events-none"
          />
          <input
            ref="searchRef"
            v-model="query"
            type="search"
            autocomplete="off"
            class="w-full h-8 !pl-8 !pr-2 text-sm rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:!bg-slate-900 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 !mb-0 [&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
            :placeholder="t('PLANNING.EVENTS.SEARCH_PLACEHOLDER')"
            @keydown.stop
          />
        </div>
        <div
          ref="listRef"
          class="flex max-h-96 flex-col gap-1 overflow-y-auto overscroll-y-contain"
        >
          <button
            v-for="option in filteredOptions"
            :key="`${option.type}-${option.key}`"
            type="button"
            class="w-full max-w-80 text-left px-3 py-2 text-sm capitalize hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors flex items-center gap-2 rounded-md"
            :class="{
              'font-medium text-n-slate-12 bg-slate-50 dark:bg-slate-700':
                currentOption?.type === option.type &&
                currentOption?.key === option.key,
            }"
            @click="selectStage(option)"
          >
            <span
              class="size-2.5 rounded-full flex-shrink-0"
              :style="{ backgroundColor: resolveStatusOptionColor(option) }"
            />
            <span class="truncate">{{ optionLabel(option) }}</span>
          </button>
        </div>
      </div>
    </template>
  </VDropdown>
</template>
